import { AreaData } from '../types/areas';
import { appDefIds } from '@wix/business-manager-api';

export const areas: AreaData[] = [
  /**
   * If you want to add a new area you should add a new entry to this array.
   * Area Fields:
   *  {
   *    // localized key of the area, you must add this key and a value to messages_en.json
   *    // value format: 'manage.areas.${area_id}.title'
   *    titleKey: 'manage.areas.editor.title',
   *
   *    // optional
   *    // if restrictFromLevel == 'site', this area won't be part of the site role areas
   *    // if restrictFromLevel == 'account', this area won't be part of the account role areas
   *    // 'site' or 'account'
   *    restrictFromLevel?: 'site',
   *
   *    // optional
   *    // only for site level (contributors)
   *    // the area will be displayed only if at least one app from the appDefIds array was added to the site
   *    // for predefined appDefIds please see https://github.com/wix-private/business-manager/blob/master/business-manager-api/src/appDefIds.ts
   *    appDefIds?: [appDefIds.wixBookings, 'bfa8c45b-be27-490c-93a8-82a337eae912']
   *
   *    scopes: [
   *      {
   *        // The server id of the scope (like defined here https://bo.wix.com/wow/permissions/scope-catalog)
   *        id: 'SCOPE.EDITOR.EDIT-SITES',
   *
   *        // localized key of the scope title, you must add this key and a value to messages_en.json
   *        // value format: `manage.roles.${permission type}.${scope id}.title`
   *        titleKey: 'manage.roles.edit_permission.edit_site.title',
   *
   *        // localized key of the scope description, you must add this key and a value to messages_en.json
   *        // value format: `manage.roles.${permission type}.${scope id}.desc`
   *        descriptionKey: 'manage.roles.edit_permission.edit_site.desc',
   *
   *        // scope level
   *        // possible values: 'account' or 'site'
   *        level: 'account',
   *
   *        // optional
   *        // set true if you want to deprecate scope,
   *        // Warning!!: if deprecated == true, this scope won't appear when user creates a new role
   *        // true or false (default)
   *        deprecated?: false,
   *
   *        // optional
   *        // if restrictFromLevel == 'site', this scope won't be part of the site (contributors) role scopes
   *        // if restrictFromLevel == 'account', this scope won't be part of the account (team) role scopes
   *        // 'site' or 'account'
   *        restrictFromLevel?: 'site',
   *
   *        // optional
   *        // experiments for this scope
   *        // if experiment added to config, the scope will be displayed only if experiment value is true
   *        // account manger scope - 'account-manager'
   *        // site manager scope - 'business-manager'
   *        experiments?: ['specs.wos.DisplayScope']
   *      }
   *    ]
   *  }
   *
   * **/
  {  // Editor
    titleKey: 'manage.roles.edit_permission.editor.area',
    scopes: [
      {
        id: 'SCOPE.EDITOR.EDIT-SITES',
        titleKey: 'manage.roles.edit_permission.edit_site.title',
        descriptionKey: 'manage.roles.edit_permission.edit_site.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.EDITOR.PUBLISH-SITES',
        titleKey: 'manage.roles.edit_permission.publish_site.title',
        descriptionKey: 'manage.roles.edit_permission.publish_site.desc',
        level: 'site',
        dependantScopes: ['SCOPE.EDITOR.EDIT-SITES'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CX-OS.CREATE-SITES',
        titleKey: 'manage.roles.edit_permission.create_sites.title',
        descriptionKey: 'manage.roles.edit_permission.create_sites.desc',
        level: 'account',
        restrictFromLevel: 'site',
        dependantScopes: ['SCOPE.EDITOR.EDIT-SITES', 'SCOPE.CX-OS.CREATE-SITES-FOR-SITE'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // OS & Premium - Sites
    titleKey: 'manage.roles.edit_permission.sites.area',
    scopes: [
      {
        id: 'SCOPE.CX-OS.DELETE-RESTORE-SITES-FOR-SITE',
        titleKey: 'manage.roles.edit_permission.remove_sites.title',
        descriptionKey: 'manage.roles.edit_permission.remove_sites.desc',
        level: 'site',
        restrictFromLevel: 'site',
        experiments: ['specs.ident.CustomRoleSitesForSiteScopes', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CX-OS.TRANSFER-SITES-FOR-SITE',
        titleKey: 'manage.roles.edit_permission.transfer_sites.title',
        descriptionKey: 'manage.roles.edit_permission.transfer_sites.desc',
        level: 'site',
        restrictFromLevel: 'site',
        experiments: ['specs.ident.CustomRoleSitesForSiteScopes', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CX-OS.DUPLICATE-SITES-FOR-SITE',
        titleKey: 'manage.roles.edit_permission.duplicate_sites.title',
        descriptionKey: 'manage.roles.edit_permission.duplicate_sites.desc',
        level: 'site',
        restrictFromLevel: 'site',
        experiments: ['specs.ident.ScopeDuplicateSitesForSite', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CX-OS.MANAGE-FOLDERS',
        titleKey: 'manage.roles.edit_permission.manage_folders.title',
        descriptionKey: 'manage.roles.edit_permission.manage_folders.desc',
        level: 'account',
        restrictFromLevel: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CX-OS.MANAGE-GENERAL-INFO',
        titleKey: 'manage.roles.edit_permission.manage_general_info.title',
        descriptionKey: 'manage.roles.edit_permission.manage_general_info.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeSitesManageGeneralInfo', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Premium - Billing & Domains & Partners - Marketplace
    titleKey: 'manage.roles.edit_permission.billing.area',
    scopes: [
      {
        id: 'SCOPE.PREMIUM.MANAGE-BILLING',
        titleKey: 'manage.roles.edit_permission.manage_billing.title',
        descriptionKey: 'manage.roles.edit_permission.manage_billing.desc',
        level: 'account',
        dependantScopes: ['SCOPE.PREMIUM.MANAGE-DOMAINS', 'SCOPE.PREMIUM.MANAGE-BILLING-FOR-SITE'],
        restrictFromLevel: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.PREMIUM.MANAGE-DOMAINS',
        titleKey: 'manage.roles.edit_permission.manage_domains.title',
        descriptionKey: 'manage.roles.edit_permission.manage_domains.desc',
        level: 'account',
        dependantScopes: ['SCOPE.PREMIUM.MANAGE-DOMAINS-FOR-SITE'],
        restrictFromLevel: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  /* {  // Premium - Billing & Domains
     titleKey: 'manage.roles.edit_permission.Marketplace.area',
     scopes: [
       {
         id: 'SCOPE.Marketplace.MANAGE-LEADS',
         titleKey: 'manage.roles.edit_permission.Marketplace_leads.title',
         descriptionKey: 'manage.roles.edit_permission.Marketplace_leads.desc',
         level: 'account',
       },
       {
         id: 'SCOPE.Marketplace.MANAGE-PROFILE',
         titleKey: 'manage.roles.edit_permission.Marketplace_profile.title',
         descriptionKey: 'manage.roles.edit_permission.Marketplace_profile.desc',
         level: 'account',
       },
     ]
   },*/
  {  // Stores - Stores
    titleKey: 'manage.roles.edit_permission.stores.area',
    appDefIds: [appDefIds.wixECommerce],
    scopes: [
      {
        id: 'SCOPE.STORES.MANAGE-STORES',
        titleKey: 'manage.roles.edit_permission.manage_stores.title',
        descriptionKey: 'manage.roles.edit_permission.manage_stores.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.STORES.MANAGE-STORES-LIMITED',
        titleKey: 'manage.roles.edit_permission.manage_stores_limited.title',
        descriptionKey: 'manage.roles.edit_permission.manage_stores_limited.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ],
  },
  {  // Stores - POS
    titleKey: 'manage.roles.edit_permission.POS.area',
    appDefIds: [appDefIds.pointOfSale],
    scopes: [
      {
        id: 'SCOPE.POS.CHECKOUT-CUSTOMERS',
        titleKey: 'manage.roles.edit_permission.checkout_customers_POS.title',
        descriptionKey: 'manage.roles.edit_permission.checkout_customers_POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.CHECKOUT-NO-SALE',
        titleKey: 'manage.roles.edit_permission.open_cash_drawer_POS.title',
        descriptionKey: 'manage.roles.edit_permission.open_cash_drawer_POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.CHECKOUT-APPLY-CUSTOM-DISCOUNT',
        titleKey: 'manage.roles.edit_permission.apply_custom_discount_POS.title',
        descriptionKey: 'manage.roles.edit_permission.apply_custom_discount_POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.REPORTS-VIEW-CURRENT-DAY',
        titleKey: 'manage.roles.edit_permission.view_current_day_POS.title',
        descriptionKey: 'manage.roles.edit_permission.view_current_day_POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.REPORTS-VIEW-PREVIOUS-DAYS',
        titleKey: 'manage.roles.edit_permission.view_previous_day_POS.title',
        descriptionKey: 'manage.roles.edit_permission.view_previous_day_POS.desc',
        level: 'site',
        dependantScopes: ['SCOPE.POS.REPORTS-VIEW-CURRENT-DAY'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.REPORTS-MANAGE-CASH-DRAWER',
        titleKey: 'manage.roles.edit_permission.manage_cash_drawer_POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_cash_drawer_POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.ORDERS-MANAGE-TODAY',
        titleKey: 'manage.roles.edit_permission.manage_orders.POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_orders.POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.ORDERS-MANAGE-PAST-DAYS',
        titleKey: 'manage.roles.edit_permission.manage_sales_history.POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_sales_history.POS.desc',
        level: 'site',
        dependantScopes: ['SCOPE.POS.ORDERS-MANAGE-TODAY'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.CUSTOMERS-VIEW-AND-CREATE',
        titleKey: 'manage.roles.edit_permission.view_profiles.POS.title',
        descriptionKey: 'manage.roles.edit_permission.view_profiles.POS.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.CUSTOMERS-EDIT',
        titleKey: 'manage.roles.edit_permission.manage_profiles.POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_profiles.POS.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS', 'SCOPE.POS.CUSTOMERS-VIEW-AND-CREATE'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.MANAGE-DISCOUNTS',
        titleKey: 'manage.roles.edit_permission.manage_discounts.POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_discounts.POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.MANAGE-PRODUCTS-COLLECTIONS',
        titleKey: 'manage.roles.edit_permission.manage_products_collections.POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_products_collections.POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.SETTINGS-CUSTOMIZE-CHECKOUT',
        titleKey: 'manage.roles.edit_permission.customize_checkout.POS.title',
        descriptionKey: 'manage.roles.edit_permission.customize_checkout.POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.SETTINGS-MANAGE-PAYMENTS',
        titleKey: 'manage.roles.edit_permission.manage_payments.POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_payments.POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.SETTINGS-MANAGE-TAX',
        titleKey: 'manage.roles.edit_permission.manage_tax.POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_tax.POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.SETTINGS-MANAGE-RECEIPTS',
        titleKey: 'manage.roles.edit_permission.manage_receipts.POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_receipts.POS.title.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.SETTINGS-MANAGE-CASH-MANAGEMENT',
        titleKey: 'manage.roles.edit_permission.manage_cash.POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_cash.POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.POS.SETTINGS-MANAGE-TEAM-ACCESS-SETTINGS',
        titleKey: 'manage.roles.edit_permission.manage_team.POS.title',
        descriptionKey: 'manage.roles.edit_permission.manage_team.POS.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Booking
    titleKey: 'manage.roles.edit_permission.bookings.area',
    appDefIds: [appDefIds.wixBookings],
    scopes: [
/*      {
        id: 'SCOPE.BOOKINGS.MANAGE-CALENDAR',
        titleKey: 'manage.roles.edit_permission.personal_bookings.title',
        descriptionKey: 'manage.roles.edit_permission.personal_bookings.desc',
        level: 'site',
      },*/
      {
        id: 'SCOPE.BOOKINGS.MANAGE-BOOKINGS',
        titleKey: 'manage.roles.edit_permission.manage_all_bookings.title',
        descriptionKey: 'manage.roles.edit_permission.manage_all_bookings.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.BOOKINGS.MANAGE-BOOKINGS-SETTINGS',
        titleKey: 'manage.roles.edit_permission.manage_bookings_settings.title',
        descriptionKey: 'manage.roles.edit_permission.manage_bookings_settings.desc',
        level: 'site',
        dependantScopes: ['SCOPE.BOOKINGS.MANAGE-BOOKINGS', 'SCOPE.PAID-PLANS.MANAGE'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.BOOKINGS.CONFIGURATION',
        titleKey: 'manage.roles.edit_permission.create_bookings_services.title',
        descriptionKey: 'manage.roles.edit_permission.create_bookings_services.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeBookingsConfiguration', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Identity - Site Members
    titleKey: 'manage.roles.edit_permission.members.area',
    scopes: [
      {
        id: 'SCOPE.IDENTITY.MANAGE-SITE-MEMBERS',
        titleKey: 'manage.roles.edit_permission.manage_members.title',
        descriptionKey: 'manage.roles.edit_permission.manage_members.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS', 'SCOPE.PROMOTE.EMAIL-MARKETING'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Blog
    titleKey: 'manage.roles.edit_permission.blog.area',
    appDefIds: [appDefIds.socialBlog],
    scopes: [
/*      {
        id: 'SCOPE.BLOG.VIEW-BLOG',
        titleKey: 'manage.roles.edit_permission.view_blog.title',
        descriptionKey: 'manage.roles.edit_permission.view_blog.desc',
        level: 'site',
        dependantScopes: ['SCOPE.IDENTITY.MANAGE-SITE-MEMBERS'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },*/
/*      {
        id: 'SCOPE.BLOG.EDIT-POSTS',
        titleKey: 'manage.roles.edit_permission.posts.title',
        descriptionKey: 'manage.roles.edit_permission.posts.desc',
        level: 'site',
        dependantScopes: ['SCOPE.BLOG.VIEW-BLOG'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },*/
      /*      {
              id: 'SCOPE.BLOG.EDIT-POST-SETTINGS',
              titleKey: 'manage.roles.edit_permission.post_settings.title',
              descriptionKey: 'manage.roles.edit_permission.post_settings.desc',
              level: 'site',
              dependantScopes: ['SCOPE.IDENTITY.MANAGE-SITE-MEMBERS'],
            },*/
      {
        id: 'SCOPE.BLOG.PUBLISH-POSTS',
        titleKey: 'manage.roles.edit_permission.publish_post.title',
        descriptionKey: 'manage.roles.edit_permission.publish_post.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.BLOG.MODERATE-BLOG',
        titleKey: 'manage.roles.edit_permission.moderate_blog.title',
        descriptionKey: 'manage.roles.edit_permission.moderate_blog.desc',
        level: 'site',
        dependantScopes: ['SCOPE.PAID-PLANS.VIEW', 'SCOPE.BLOG.PUBLISH-POSTS'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.BLOG.MANAGE-BLOG',
        titleKey: 'manage.roles.edit_permission.manage_blog_settings.title',
        descriptionKey: 'manage.roles.edit_permission.manage_blog_settings.desc',
        level: 'site',
        dependantScopes: ['SCOPE.BLOG.MODERATE-BLOG'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Paid Plans - Paid Plans
    titleKey: 'manage.roles.edit_permission.paid_plans.area',
    appDefIds: [appDefIds.membership],
    scopes: [
      {
        id: 'SCOPE.PAID-PLANS.VIEW',
        titleKey: 'manage.roles.edit_permission.view_paid_plans.title',
        descriptionKey: 'manage.roles.edit_permission.view_paid_plans.desc',
        level: 'site',
        dependantScopes: ['SCOPE.SUBSCRIPTIONS.VIEW'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.PAID-PLANS.MANAGE',
        titleKey: 'manage.roles.edit_permission.manage_paid_plans.title',
        descriptionKey: 'manage.roles.edit_permission.manage_paid_plans.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.SETUP-AUTOMATIONS', 'SCOPE.PROMOTE.TRIGGERED-EMAILS', 'SCOPE.PAID-PLANS.VIEW', 'SCOPE.SUBSCRIPTIONS.MANAGE'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Subscriptions - Subscriptions
    titleKey: 'manage.roles.edit_permission.subscriptions.area',
    appDefIds: [appDefIds.subscriptions],
    scopes: [
      {
        id: 'SCOPE.SUBSCRIPTIONS.VIEW',
        titleKey: 'manage.roles.edit_permission.view_subscriptions.title',
        descriptionKey: 'manage.roles.edit_permission.view_subscriptions.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.SUBSCRIPTIONS.MANAGE',
        titleKey: 'manage.roles.edit_permission.manage_subscriptions.title',
        descriptionKey: 'manage.roles.edit_permission.manage_subscriptions.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
        dependantScopes: ['SCOPE.SUBSCRIPTIONS.VIEW'],
      },
    ]
  },
  {  // Events - Events
    titleKey: 'manage.roles.edit_permission.events.area',
    appDefIds: [appDefIds.events],
    scopes: [
      {
        id: 'SCOPE.EVENTS.MANAGE-CHECK-INS',
        titleKey: 'manage.roles.edit_permission.checkin_guests.title',
        descriptionKey: 'manage.roles.edit_permission.checkin_guests.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // CRM - Customer Management
    titleKey: 'manage.roles.edit_permission.CRM.area',
    scopes: [
      /*      {
              id: 'SCOPE.CRM.VIEW-CONTACTS',
              titleKey: 'manage.roles.edit_permission.view_contacts.title',
              descriptionKey: 'manage.roles.edit_permission.view_contacts.desc',
              level: 'site',
            },*/
      {
        id: 'SCOPE.CHAT.COMMUNICATE-INBOX-CHAT',
        titleKey: 'manage.roles.edit_permission.manage_inbox.title',
        descriptionKey: 'manage.roles.edit_permission.manage_inbox.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CHAT.INBOX-CHAT-SETTINGS',
        titleKey: 'manage.roles.edit_permission.inbox_settings.title',
        descriptionKey: 'manage.roles.edit_permission.inbox_settings.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CHAT.COMMUNICATE-INBOX-CHAT'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CRM.MANAGE-CONTACTS',
        titleKey: 'manage.roles.edit_permission.manage_contacts.title',
        descriptionKey: 'manage.roles.edit_permission.manage_contacts.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CRM.EXPORT-CONTACTS',
        titleKey: 'manage.roles.edit_permission.import_contacts.title',
        descriptionKey: 'manage.roles.edit_permission.import_contacts.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CRM.VIEW-FORM-DATA',
        titleKey: 'manage.roles.edit_permission.form_submissions.title',
        descriptionKey: 'manage.roles.edit_permission.form_submissions.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CRM.MANAGE-TASKS',
        titleKey: 'manage.roles.edit_permission.manage_tasks.title',
        descriptionKey: 'manage.roles.edit_permission.manage_tasks.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CRM.MANAGE-WORKFLOWS',
        titleKey: 'manage.roles.edit_permission.manage_workflows.title',
        descriptionKey: 'manage.roles.edit_permission.manage_workflows.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS', 'SCOPE.CRM.MANAGE-TASKS'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CRM.SETUP-AUTOMATIONS',
        titleKey: 'manage.roles.edit_permission.manage_automations.title',
        descriptionKey: 'manage.roles.edit_permission.manage_automations.desc',
        level: 'site',
        dependantScopes: ['SCOPE.PROMOTE.TRIGGERED-EMAILS'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CRM.VIEW-BUSINESS-NUMBER',
        titleKey: 'manage.roles.edit_permission.manage_calls.title',
        descriptionKey: 'manage.roles.edit_permission.manage_calls.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeCrmViewBusinessNumber', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CRM.MANAGE-BUSINESS-NUMBER',
        titleKey: 'manage.roles.edit_permission.manage_business_number.title',
        descriptionKey: 'manage.roles.edit_permission.manage_business_number.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.VIEW-BUSINESS-NUMBER'],
        experiments: ['specs.ident.ScopeCrmManageBusinessNumber', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Promote & Media - Marketing Tools
    titleKey: 'manage.roles.edit_permission.Marketing.area',
    scopes: [
      {
        id: 'SCOPE.STORES.MANAGE-COUPONS',
        titleKey: 'manage.roles.edit_permission.manage_coupons.title',
        descriptionKey: 'manage.roles.edit_permission.manage_coupons.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction',  'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.PROMOTE.MANAGE-SOCIAL-POSTS',
        titleKey: 'manage.roles.edit_permission.manage_social_posts.title',
        descriptionKey: 'manage.roles.edit_permission.manage_social_posts.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.PROMOTE.MANAGE-FB-CAMPAIGNS',
        titleKey: 'manage.roles.edit_permission.facebook_ads_campaigns.title',
        descriptionKey: 'manage.roles.edit_permission.facebook_ads_campaigns.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction',  'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.PROMOTE.MANAGE-GOOGLE-MY-BUSINESS',
        titleKey: 'manage.roles.edit_permission.google_my_biz.title',
        descriptionKey: 'manage.roles.edit_permission.google_my_biz.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction',  'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.PROMOTE.MANAGE-SEO',
        titleKey: 'manage.roles.edit_permission.manage_seo_settings.title',
        descriptionKey: 'manage.roles.edit_permission.manage_seo_settings.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.PROMOTE.MARKETING-INTEGRATIONS',
        titleKey: 'manage.roles.edit_permission.marketing-integrations.title',
        descriptionKey: 'manage.roles.edit_permission.marketing-integrations.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.PROMOTE.TRIGGERED-EMAILS',
        titleKey: 'manage.roles.edit_permission.triggered_emails.title',
        descriptionKey: 'manage.roles.edit_permission.triggered_emails.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.PROMOTE.EMAIL-MARKETING',
        titleKey: 'manage.roles.edit_permission.email_campaigns.title',
        descriptionKey: 'manage.roles.edit_permission.email_campaigns.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.MEDIA.MANAGE-VIDEO-MAKER',
        titleKey: 'manage.roles.edit_permission.manage_video_maker.title',
        descriptionKey: 'manage.roles.edit_permission.manage_video_maker.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Media - Analytics
    titleKey: 'manage.roles.edit_permission.Analytics.area',
    scopes: [
      {
        id: 'SCOPE.MEDIA.VIEW-SITE-ANALYTICS',
        titleKey: 'manage.roles.edit_permission.view_analytics.title',
        descriptionKey: 'manage.roles.edit_permission.view_analytics.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Payments - Payments
    titleKey: 'manage.roles.edit_permission.payment.area',
    scopes: [
      {
        id: 'SCOPE.PAYMENTS.MANAGE-PAYMENT-SETTINGS',
        titleKey: 'manage.roles.edit_permission.payment_settings.title',
        descriptionKey: 'manage.roles.edit_permission.payment_settings.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.PAYMENTS.VIEW-PAYMENTS',
        titleKey: 'manage.roles.edit_permission.manage_payments.title',
        descriptionKey: 'manage.roles.edit_permission.manage_payments.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.CRM.MANAGE-INVOICES-AND-QUOTES',
        titleKey: 'manage.roles.edit_permission.manage_invoices_quotes.title',
        descriptionKey: 'manage.roles.edit_permission.manage_invoices_quotes.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS', 'SCOPE.SUBSCRIPTIONS.MANAGE'],
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Media - Media
    titleKey: 'manage.roles.edit_permission.media.area',
    appDefIds: [appDefIds.wixMusic, appDefIds.video],
    scopes: [
      {
        id: 'SCOPE.MEDIA.EDIT-MUSIC-LIBRARY',
        titleKey: 'manage.roles.edit_permission.music_library.title',
        descriptionKey: 'manage.roles.edit_permission.music_library.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.MEDIA.EDIT-VIDEO-LIBRARY',
        titleKey: 'manage.roles.edit_permission.video_library.title',
        descriptionKey: 'manage.roles.edit_permission.video_library.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeEditVideoLibrary', 'specs.ident.CustomRoleAllScopes'],
      },
      /*{
        id: 'SCOPE.MEDIA.EDIT-MUSIC-PLAYER',
        titleKey: 'manage.roles.edit_permission.music_player.title',
        descriptionKey: 'manage.roles.edit_permission.music_player.desc',
        level: 'site',
      },
      {
        id: 'SCOPE.MEDIA.EDIT-VIDEO-PLAYER',
        titleKey: 'manage.roles.edit_permission.video_player.title',
        descriptionKey: 'manage.roles.edit_permission.video_player.desc',
        level: 'site',
      },*/
    ]
  },
  {  // Photographers - Photo Albums
    titleKey: 'manage.roles.edit_permission.photo_albums.area',
    appDefIds: [appDefIds.photographers],
    scopes: [
      {
        id: 'SCOPE.PHOTOGRAPHERS.MANAGE-ALBUMS',
        titleKey: 'manage.roles.edit_permission.manage_albums.title',
        descriptionKey: 'manage.roles.edit_permission.manage_albums.desc',
        level: 'site',
        experiments: ['specs.ident.ScopePhotographersManageAlbums', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Photographers - Art Store
    titleKey: 'manage.roles.edit_permission.art_store.area',
    appDefIds: [appDefIds.artStore],
    scopes: [
      {
        id: 'SCOPE.PHOTOGRAPHERS.MANAGE-ART-STORE',
        titleKey: 'manage.roles.edit_permission.manage_art_store.title',
        descriptionKey: 'manage.roles.edit_permission.manage_art_store.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Logo Maker - Logo Maker
    titleKey: 'manage.roles.edit_permission.logo.area',
    appDefIds: [appDefIds.artStore],
    scopes: [
      {
        id: 'SCOPE.LOGO-MAKER.MANAGE-LOGOS',
        titleKey: 'manage.roles.edit_permission.manage_logos.title',
        descriptionKey: 'manage.roles.edit_permission.manage_logos.desc',
        level: 'account',
        restrictFromLevel: 'site',
        experiments: ['specs.ident.ScopeLogoMakerManageLogos', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  { // Forum
    titleKey: 'manage.roles.edit_permission.forum.area',
    appDefIds: [appDefIds.forum],
    scopes: [
      {
        id: 'SCOPE.FORUM.READ',
        titleKey: 'manage.roles.edit_permission.forum_read.title',
        descriptionKey: 'manage.roles.edit_permission.forum_read.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeForumRead', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.FORUM.WRITE',
        titleKey: 'manage.roles.edit_permission.forum_write.title',
        descriptionKey: 'manage.roles.edit_permission.forum_write.desc',
        level: 'site',
        dependantScopes: ['SCOPE.FORUM.READ'],
        experiments: ['specs.ident.ScopeForumWrite', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.FORUM.ALL-CATEGORY-ACCESS',
        titleKey: 'manage.roles.edit_permission.forum_all_category_access.title',
        descriptionKey: 'manage.roles.edit_permission.forum_all_category_access.desc',
        level: 'site',
        dependantScopes: ['SCOPE.FORUM.READ'],
        experiments: ['specs.ident.ScopeForumAllCategoryAccess', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.FORUM.MODERATE-FORUM',
        titleKey: 'manage.roles.edit_permission.forum_moderate.title',
        descriptionKey: 'manage.roles.edit_permission.forum_moderate.desc',
        level: 'site',
        dependantScopes: ['SCOPE.FORUM.WRITE'],
        experiments: ['specs.ident.ScopeForumModerateForum', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.FORUM.MANAGE-CATEGORIES',
        titleKey: 'manage.roles.edit_permission.forum_manage_categories.title',
        descriptionKey: 'manage.roles.edit_permission.forum_manage_categories.desc',
        level: 'site',
        dependantScopes: ['SCOPE.FORUM.ALL-CATEGORY-ACCESS'],
        experiments: ['specs.ident.ScopeForumManageCategories', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.FORUM.ADMIN',
        titleKey: 'manage.roles.edit_permission.forum_admin.title',
        descriptionKey: 'manage.roles.edit_permission.forum_admin.desc',
        level: 'site',
        dependantScopes: ['SCOPE.FORUM.MODERATE-FORUM', 'SCOPE.FORUM.MANAGE-CATEGORIES', 'SCOPE.FORUM.ALL-CATEGORY-ACCESS'],
        experiments: ['specs.ident.ScopeForumAdmin', 'specs.ident.CustomRoleAllScopes'],
      },
    ],
  },
  {  // Groups - Groups
    titleKey: 'manage.roles.edit_permission.groups.area',
    appDefIds: [appDefIds.wixGroups],
    scopes: [
      {
        id: 'SCOPE.GROUPS.CREATE-AND-MANAGE',
        titleKey: 'manage.roles.edit_permission.create_manage_groups.title',
        descriptionKey: 'manage.roles.edit_permission.create_manage_groups.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeGroupsCreateAndManage', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // Restaurants - Restaurants
    titleKey: 'manage.roles.edit_permission.restaurants.area',
    appDefIds: [appDefIds.restaurants, appDefIds.restaurantsKit, appDefIds.restaurantsOrders, appDefIds.restaurantsSeating],
    scopes: [
      {
        id: 'SCOPE.RESTAURANTS.MANAGE-RESTAURANTS',
        titleKey: 'manage.roles.edit_permission.manage_restaurant_business.title',
        descriptionKey: 'manage.roles.edit_permission.manage_restaurant_business.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeRestaurantsManageRestaurants', 'specs.ident.CustomRoleAllScopes'],
      },
      /*{
        id: 'SCOPE.RESTAURANTS.OPERATE-RESTAURANTS',
        titleKey: 'manage.roles.edit_permission.manage_restaurant_apps.title',
        descriptionKey: 'manage.roles.edit_permission.manage_restaurant_apps.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeRestaurantsOperateRestaurants', 'specs.ident.CustomRoleAllScopes'],
      },*/
    ]
  },
  /*{  // Data - Data
    titleKey: 'manage.roles.edit_permission.data.area',
    appDefIds: [appDefIds.wixCode],
    scopes: [
      {
        id: 'SCOPE.DATA.DATA-READ-ACCESS',
        titleKey: 'manage.roles.edit_permission.read_data.title',
        descriptionKey: 'manage.roles.edit_permission.read_data.desc',
        level: 'site',
      },
      {
        id: 'SCOPE.DATA.DATA-WRITE-ACCESS',
        titleKey: 'manage.roles.edit_permission.write_data.title',
        descriptionKey: 'manage.roles.edit_permission.write_data.desc',
        level: 'site',
      },
      {
        id: 'SCOPE.DATA.DATA-VIEWS',
        titleKey: 'manage.roles.edit_permission.manage_views.title',
        descriptionKey: 'manage.roles.edit_permission.manage_views.desc',
        level: 'site',
      },
      {
        id: 'SCOPE.DATA.DATA-VIEW-MANAGEMENT',
        titleKey: 'manage.roles.edit_permission.view_schemas.title',
        descriptionKey: 'manage.roles.edit_permission.view_schemas.desc',
        level: 'site',
      },
      {
        id: 'SCOPE.DATA.DATA-MANAGEMENT',
        titleKey: 'manage.roles.edit_permission.manage_schemas.title',
        descriptionKey: 'manage.roles.edit_permission.manage_schemas.desc',
        level: 'site',
      },
      {
        id: 'SCOPE.DATA.SANDBOX-DATA-READ',
        titleKey: 'manage.roles.edit_permission.sandbox_data_read.title',
        descriptionKey: 'manage.roles.edit_permission.sandbox_data_read.desc',
        level: 'site',
      },
    ]
  },*/
  {  // Corvid - Corvid
    titleKey: 'manage.roles.edit_permission.corvid.area',
    appDefIds: [appDefIds.wixCode],
    scopes: [
      {
        id: 'SCOPE.CORVID.ACCESS-PRODUCTION-TOOLS',
        titleKey: 'manage.roles.edit_permission.production_tools.title',
        descriptionKey: 'manage.roles.edit_permission.production_tools.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
        dependantScopes: ['SCOPE.EDITOR.PUBLISH-SITES'],
      },
      {
        id: 'SCOPE.CX-OS.VIEW-ADMIN-PAGES',
        titleKey: 'manage.roles.edit_permission.view_dashboard_pages.title',
        descriptionKey: 'manage.roles.edit_permission.view_dashboard_pages.desc',
        level: 'site',
        experiments: ['specs.ident.CustomRoleProduction', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  /*{  // Hotels - Hotels
    titleKey: 'manage.roles.edit_permission.hotels.area',

    appDefIds: [appDefIds.hotels],
    scopes: [
      {
        id: 'SCOPE.Marketplace.MANAGE-LEADS',
        titleKey: 'manage.roles.edit_permission.manage_properties.hotel.title',
        descriptionKey: 'manage.roles.edit_permission.manage_properties.hotel.desc',
        level: 'site',
      },
    ]
  },*/
  /*{  // OneApp - Wix App
    titleKey: 'manage.roles.edit_permission.one_app.area',
    scopes: [
      {
        id: 'SCOPE.WIXAPP.INVITE',
        titleKey: 'manage.roles.edit_permission.invite.one_app.title',
        descriptionKey: 'manage.roles.edit_permission.invite.one_app.desc',
        level: 'site',
        dependantScopes: ['SCOPE.CRM.MANAGE-CONTACTS'],
      },
    ]
  },*/
  {  // Achievements - Challenges
    titleKey: 'manage.roles.edit_permission.challenges.area',
    appDefIds: [appDefIds.challenges],
    scopes: [
      {
        id: 'SCOPE.CHALLENGES.MANAGE',
        titleKey: 'manage.roles.edit_permission.challenge_moderator.title',
        descriptionKey: 'manage.roles.edit_permission.challenge_moderator.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeChallengesManages', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
  {  // App Market
    titleKey: 'manage.roles.edit_permission.app_market.area',
    scopes: [
      {
        id: 'SCOPE.APP-MARKET.INSTALL-APPS',
        titleKey: 'manage.roles.edit_permission.install_apps.title',
        descriptionKey: 'manage.roles.edit_permission.install_apps.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeAppMarketInstallApps', 'specs.ident.CustomRoleAllScopes'],
      },
      {
        id: 'SCOPE.APP-MARKET.MANAGE-APPS',
        titleKey: 'manage.roles.edit_permission.manage_apps.title',
        descriptionKey: 'manage.roles.edit_permission.manage_apps.desc',
        level: 'site',
        experiments: ['specs.ident.ScopeAppMarketManageApps', 'specs.ident.CustomRoleAllScopes'],
      },
    ]
  },
];
